import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { CameFrom, Client, ClientAttributes } from '@/lib/Client';
import { CameFrom as CameFromEnum } from '@/lib/Client';
import type { Lesson, LessonAttributes } from '@/lib/Lesson';
import type { Payment, PaymentAttributes, PaymentBy } from '@/lib/Payment';
import type { Student } from '@/lib/Student';
import { t } from '@/lib/i18n';
import { TELEGRAM_BOT_USERNAME } from '@/lib/env';
import type { Subject } from '@/lib/Subject';
import { getSubjectPrettyName } from '@/lib/Subject';
import type { Teacher } from '@/lib/Teacher';
import {
    Box, Button, Card, CloseButton, Drawer, Heading, HStack, Input, Portal, Skeleton, Stat, Table, Text, Tooltip, VStack
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { mutate as globalMutate } from 'swr';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

function formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('uk-UA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function nowLocal(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const selectStyle = {
    width: '100%', padding: '4px 8px', borderRadius: '4px',
    border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
};

const CAME_FROM_OPTIONS: CameFrom[] = [
    CameFromEnum.Advertisement, CameFromEnum.Instagram, CameFromEnum.Client,
    CameFromEnum.Manager, CameFromEnum.Teacher, CameFromEnum.Website, CameFromEnum.Search, CameFromEnum.Other,
];

const cameFromLabels: Record<CameFrom, string> = {
    [CameFromEnum.Advertisement]: t('came_from.advertisement'),
    [CameFromEnum.Instagram]: t('came_from.instagram'),
    [CameFromEnum.Client]: t('came_from.client'),
    [CameFromEnum.Manager]: t('came_from.manager'),
    [CameFromEnum.Teacher]: t('came_from.teacher'),
    [CameFromEnum.Website]: t('came_from.website'),
    [CameFromEnum.Search]: t('came_from.search'),
    [CameFromEnum.Other]: t('came_from.other'),
};

function ClientEditDrawer({ client, open, onClose, onSave }: {
    client: Client; open: boolean; onClose: () => void; onSave: (a: ClientAttributes) => Promise<void>;
}) {
    const [name, setName] = useState(client.name);
    const [phone, setPhone] = useState(client.phone ?? '');
    const [email, setEmail] = useState(client.email ?? '');
    const [instagram, setInstagram] = useState(client.instagram ?? '');
    const [telegram, setTelegram] = useState(client.telegram ?? '');
    const [comment, setComment] = useState(client.comment ?? '');
    const [cameFrom, setCameFrom] = useState<CameFrom>(client.came_from);
    const [hidden, setHidden] = useState(client.hidden ?? false);
    const [deleted, setDeleted] = useState(client.deleted);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        if (!name.trim()) { setError(t('clients.name_required')); return; }
        setSubmitting(true); setError('');
        try {
            await onSave({ name: name.trim(), phone: phone.trim() || undefined, email: email.trim() || undefined, instagram: instagram.trim() || undefined, telegram: telegram.trim() || undefined, comment: comment.trim() || undefined, came_from: cameFrom, hidden, deleted });
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{t('client_detail.edit')}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('label.name')} *</Text><Input size="sm" value={name} onChange={e => setName(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.phone')}</Text><Input size="sm" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+380..." /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.email')}</Text><Input size="sm" value={email} onChange={e => setEmail(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.instagram')}</Text><Input size="sm" value={instagram} onChange={e => setInstagram(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.telegram')}</Text><Input size="sm" value={telegram} onChange={e => setTelegram(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.came_from')}</Text>
                        <select value={cameFrom} onChange={e => setCameFrom(e.target.value as CameFrom)} style={selectStyle}>
                            {CAME_FROM_OPTIONS.map(o => <option key={o} value={o}>{cameFromLabels[o]}</option>)}
                        </select>
                    </Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} /> {t('label.hidden')}</label>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} /> {t('label.deleted')}</label>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

function StudentFormDrawer({ title, initial, open, onClose, onSave }: {
    title: string;
    initial: { name?: string; date_of_birth?: string; comment?: string; deleted?: boolean };
    open: boolean; onClose: () => void;
    onSave: (data: { name: string; date_of_birth?: string; comment?: string; deleted: boolean }) => Promise<void>;
}) {
    const [name, setName] = useState(initial.name ?? '');
    const [dob, setDob] = useState(initial.date_of_birth ? initial.date_of_birth.slice(0, 10) : '');
    const [comment, setComment] = useState(initial.comment ?? '');
    const [deleted, setDeleted] = useState(initial.deleted ?? false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        if (!name.trim()) { setError(t('student.name_required')); return; }
        setSubmitting(true); setError('');
        try {
            await onSave({ name: name.trim(), date_of_birth: dob || undefined, comment: comment.trim() || undefined, deleted });
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{title}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('label.name')} *</Text><Input size="sm" value={name} onChange={e => setName(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.date_of_birth')}</Text><Input size="sm" type="date" value={dob} onChange={e => setDob(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} /> {t('label.deleted')}</label>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

function PaymentFormDrawer({ title, initial, clientId, open, onClose, onSave, getAccessToken }: {
    title: string; initial: Partial<PaymentAttributes>; clientId: string;
    open: boolean; onClose: () => void; onSave: (attrs: PaymentAttributes) => Promise<string | undefined>;
    getAccessToken: () => Promise<string>;
}) {
    const [amount, setAmount] = useState(String(initial.amount ?? ''));
    const [dateTime, setDateTime] = useState(initial.date_time ? initial.date_time.slice(0, 16) : nowLocal());
    const [comment, setComment] = useState(initial.comment ?? '');
    const [paymentBy, setPaymentBy] = useState<PaymentBy>(initial.payment_by ?? 'cash');
    const [deleted, setDeleted] = useState(initial.deleted ?? false);
    const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        const amt = parseInt(amount, 10);
        if (isNaN(amt) || amt < 0) { setError(t('payments.amount_invalid')); return; }
        if (!dateTime) { setError(t('payments.datetime_required')); return; }
        setSubmitting(true); setError('');
        try {
            const paymentId = await onSave({ client_id: clientId, amount: amt, date_time: new Date(dateTime).toISOString(), comment: comment.trim() || undefined, payment_by: paymentBy, deleted });
            if (paymentId && attachmentFile) {
                const token = await getAccessToken();
                const res = await apiRequest<{ upload_url: string }>('POST', `/payments/${paymentId}/attachment/upload`, token);
                await fetch(res.upload_url, { method: 'PUT', body: attachmentFile });
            }
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{title}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('label.amount')} (₴) *</Text><Input size="sm" type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.date')} *</Text><Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.payment_by')} *</Text>
                        <select value={paymentBy} onChange={e => setPaymentBy(e.target.value as PaymentBy)} style={selectStyle}>
                            <option value="cash">{t('payment_by.cash')}</option><option value="card">{t('payment_by.card')}</option>
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <Box>
                        <Text fontSize="sm" mb="1">{t('label.receipt')}</Text>
                        <input type="file" accept="image/*" hidden id="payment-attachment-detail" onChange={e => setAttachmentFile(e.target.files?.[0] ?? null)} />
                        <Button size="xs" variant="outline" onClick={() => document.getElementById('payment-attachment-detail')?.click()}>
                            {attachmentFile ? attachmentFile.name : t('action.choose_file')}
                        </Button>
                        {attachmentFile && (
                            <Button size="xs" variant="ghost" ml={2} onClick={() => setAttachmentFile(null)}>{t('action.clear')}</Button>
                        )}
                    </Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} /> {t('label.deleted')}</label>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

function LessonFormDrawer({ title, initial, students, teachers, subjects, priceMap, open, onClose, onSave }: {
    title: string; initial: Partial<LessonAttributes>; students: Student[]; teachers: Teacher[]; subjects: Subject[];
    priceMap: Record<string, { price_uah: number; duration_minutes: number }>;
    open: boolean; onClose: () => void; onSave: (attrs: LessonAttributes) => Promise<void>;
}) {
    const activeStudents = students.filter(s => !s.deleted);
    const activeTeachers = teachers.filter(t => !t.deleted);
    const activeSubjects = subjects.filter(s => !s.deleted);
    const [studentId, setStudentId] = useState(initial.student_id ?? (activeStudents[0]?.id ?? ''));
    const [teacherId, setTeacherId] = useState(initial.teacher_id ?? (activeTeachers[0]?.id ?? ''));
    const [dateTime, setDateTime] = useState(
        initial.date_time_local ? initial.date_time_local.slice(0, 16).replace(' ', 'T') : nowLocal()
    );
    const [subject, setSubject] = useState(initial.subject ?? '');

    // Apply price matrix default when teacher or subject changes
    const defaultPrice = useMemo(() => {
        if (!teacherId || !subject) return null;
        return priceMap[`${teacherId}:${subject}`] ?? null;
    }, [teacherId, subject, priceMap]);

    const [priceUah, setPriceUah] = useState(String(initial.price_uah ?? '0'));
    const [durationMin, setDurationMin] = useState(String(initial.duration_minutes ?? '60'));

    // When default price changes and the field hasn't been manually edited, update it
    const [manualEdit, setManualEdit] = useState(false);
    useEffect(() => {
        if (defaultPrice && !manualEdit && !initial.price_uah) {
            setPriceUah(String(defaultPrice.price_uah));
            setDurationMin(String(defaultPrice.duration_minutes));
        }
    }, [defaultPrice, manualEdit, initial.price_uah]);
    const [individual, setIndividual] = useState(initial.individual ?? false);
    const [online, setOnline] = useState(initial.online ?? false);
    const [completed, setCompleted] = useState(initial.completed ?? false);
    const [comment, setComment] = useState(initial.comment ?? '');
    const [deleted, setDeleted] = useState(initial.deleted ?? false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    async function handleSubmit() {
        if (!studentId) { setError(t('lessons.student_required')); return; }
        if (!teacherId) { setError(t('lessons.teacher_required')); return; }
        const price = parseInt(priceUah, 10);
        const dur = parseInt(durationMin, 10);
        if (isNaN(price) || price < 0) { setError(t('lessons.price_invalid')); return; }
        if (isNaN(dur) || dur <= 0) { setError(t('lessons.duration_invalid')); return; }
        setSubmitting(true); setError('');
        try {
            const dtLocal = dateTime.replace('T', ' ') + ':00';
            await onSave({ student_id: studentId, teacher_id: teacherId, date_time_local: dtLocal, price_uah: price, duration_minutes: dur, subject: (subject || undefined), individual, online, completed, comment: comment.trim() || undefined, deleted });
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : t('error.unknown')); }
        finally { setSubmitting(false); }
    }

    return (
        <Drawer.Root open={open} onOpenChange={e => { if (!e.open) onClose(); }} placement="end">
            <Portal><Drawer.Backdrop /><Drawer.Positioner><Drawer.Content>
                <Drawer.Header><Drawer.Title>{title}</Drawer.Title></Drawer.Header>
                <Drawer.Body><VStack gap={3} align="stretch">
                    {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                    <Box><Text fontSize="sm" mb="1">{t('label.student')} *</Text>
                        <select value={studentId} onChange={e => setStudentId(e.target.value)} style={selectStyle}>
                            {activeStudents.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.teacher')} *</Text>
                        <select value={teacherId} onChange={e => setTeacherId(e.target.value)} style={selectStyle}>
                            {activeTeachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.date')} *</Text><Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.price')} (₴)</Text><Input size="sm" type="number" min="0" value={priceUah} onChange={e => setPriceUah(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.duration')}</Text><Input size="sm" type="number" min="1" value={durationMin} onChange={e => setDurationMin(e.target.value)} /></Box>
                    <Box><Text fontSize="sm" mb="1">{t('label.subject')}</Text>
                        <select value={subject} onChange={e => setSubject(e.target.value)} style={selectStyle}>
                            <option value="">{t('subject.none')}</option>
                            {activeSubjects.map(s => <option key={s.id} value={s.name}>{getSubjectPrettyName(s.name)}</option>)}
                        </select>
                    </Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={individual} onChange={e => setIndividual(e.target.checked)} /> {t('label.individual')}</label>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={online} onChange={e => setOnline(e.target.checked)} /> {t('label.online')}</label>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={completed} onChange={e => setCompleted(e.target.checked)} /> {t('label.completed')}</label>
                    <Box><Text fontSize="sm" mb="1">{t('label.comment')}</Text><Input size="sm" value={comment} onChange={e => setComment(e.target.value)} /></Box>
                    <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}><input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} /> {t('label.deleted')}</label>
                </VStack></Drawer.Body>
                <Drawer.Footer><HStack><Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button><Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button></HStack></Drawer.Footer>
                <Drawer.CloseTrigger asChild><CloseButton size="xs" variant="solid" /></Drawer.CloseTrigger>
            </Drawer.Content></Drawer.Positioner></Portal>
        </Drawer.Root>
    );
}

export function ClientDetail() {
    const { id } = useParams<{ id: string }>();
    const { getAccessTokenSilently } = useAuth0();

    const navigate = useNavigate();
    const { data: clientData, error: clientError, isLoading: clientLoading, isAuthenticated, mutate: mutateClient } =
        useAuthorizedRequest<{ client: Client }>(`/clients/${id}?populate=students`, jsonFetcher);
    const { data: paymentsData, isLoading: paymentsLoading, mutate: mutatePayments } =
        useAuthorizedRequest<{ payments: Payment[] }>(`/payments?client_id=${id}`, jsonFetcher);
    const { data: lessonsData, isLoading: lessonsLoading, mutate: mutateLessons } =
        useAuthorizedRequest<{ lessons: Lesson[] }>(`/clients/${id}/lessons`, jsonFetcher);
    const { data: teachersData } =
        useAuthorizedRequest<{ teachers: Teacher[] }>('/teachers', jsonFetcher);
    const { data: subjectsData } =
        useAuthorizedRequest<{ subjects: Subject[] }>('/subjects', jsonFetcher);
    const { data: priceMatrixData } =
        useAuthorizedRequest<{ entries: { teacher_id: string; subject: string; price_uah: number; duration_minutes: number }[] }>('/price-matrix', jsonFetcher);

    // Build price lookup: "teacher_id:subject" → { price_uah, duration_minutes }
    const priceMap = useMemo(() => {
        const map: Record<string, { price_uah: number; duration_minutes: number }> = {};
        for (const e of priceMatrixData?.entries ?? []) {
            map[`${e.teacher_id}:${e.subject}`] = { price_uah: e.price_uah, duration_minutes: e.duration_minutes };
        }
        return map;
    }, [priceMatrixData]);

    const [editClientOpen, setEditClientOpen] = useState(false);
    const [addStudentOpen, setAddStudentOpen] = useState(false);
    const [editStudent, setEditStudent] = useState<Student | null>(null);
    const [addPaymentOpen, setAddPaymentOpen] = useState(false);
    const [addLessonOpen, setAddLessonOpen] = useState(false);
    const [addLessonPresetStudent, setAddLessonPresetStudent] = useState<string | null>(null);
    const [editLesson, setEditLesson] = useState<Lesson | null>(null);

    const balance = useMemo(() => {
        const paid = (paymentsData?.payments ?? []).filter(p => !p.deleted).reduce((s, p) => s + p.amount, 0);
        const used = (lessonsData?.lessons ?? []).filter(l => !l.deleted && l.completed).reduce((s, l) => s + l.price_uah, 0);
        const pending = (lessonsData?.lessons ?? []).filter(l => !l.deleted && !l.completed).reduce((s, l) => s + l.price_uah, 0);
        return { paid, used, pending, available: paid - used - pending };
    }, [paymentsData, lessonsData]);

    const sortedStudents = useMemo(() => [...(clientData?.client?.students ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)), [clientData]);
    const sortedPayments = useMemo(() => [...(paymentsData?.payments ?? [])].filter(p => !p.deleted).sort((a, b) => b.created_at.localeCompare(a.created_at)), [paymentsData]);
    const sortedLessons = useMemo(() => [...(lessonsData?.lessons ?? [])].filter(l => !l.deleted).sort((a, b) => b.date_time_local.localeCompare(a.date_time_local)), [lessonsData]);
    const teachers = useMemo(() => teachersData?.teachers ?? [], [teachersData]);
    const subjects = useMemo(() => subjectsData?.subjects ?? [], [subjectsData]);

    const teacherMap = useMemo(() => {
        const map: Record<string, Teacher> = {};
        for (const t of teachers) map[t.id] = t;
        return map;
    }, [teachers]);

    async function handleEditClient(attrs: ClientAttributes) {
        const token = await getAccessTokenSilently();
        await apiRequest('PUT', `/clients/${id}`, token, attrs);
        await mutateClient();
    }

    async function handleAddStudent(data: { name: string; date_of_birth?: string; comment?: string; deleted: boolean }) {
        const token = await getAccessTokenSilently();
        const body: Record<string, unknown> = { name: data.name, comment: data.comment };
        if (data.date_of_birth) {
            // Convert YYYY-MM-DD to RFC3339 format for the Go backend
            body.date_of_birth = data.date_of_birth + 'T00:00:00Z';
        }
        await apiRequest('POST', `/clients/${id}/students`, token, body);
        await mutateClient();
    }

    async function handleEditStudent(data: { name: string; date_of_birth?: string; comment?: string; deleted: boolean }) {
        if (!editStudent) return;
        const token = await getAccessTokenSilently();
        const body: Record<string, unknown> = { name: data.name, comment: data.comment, deleted: data.deleted };
        if (data.date_of_birth) {
            // Convert YYYY-MM-DD to RFC3339 format for the Go backend
            body.date_of_birth = data.date_of_birth + 'T00:00:00Z';
        }
        await apiRequest('PUT', `/students/${editStudent.id}`, token, body);
        await mutateClient();
    }

    async function handleAddPayment(attrs: PaymentAttributes): Promise<string | undefined> {
        const token = await getAccessTokenSilently();
        const result = await apiRequest<{ payment: { id: string } }>('POST', '/payments', token, attrs);
        await mutatePayments();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
        return result?.payment?.id;
    }

    async function handleAddLesson(attrs: LessonAttributes) {
        const token = await getAccessTokenSilently();
        await apiRequest('POST', '/lessons', token, attrs);
        await mutateLessons();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
    }

    async function handleEditLesson(attrs: LessonAttributes) {
        if (!editLesson) return;
        const token = await getAccessTokenSilently();
        const teacherDatetime = `${editLesson.teacher_id}_${editLesson.date_time_local}`;
        await apiRequest('PUT', `/lessons/${editLesson.student_id}/${teacherDatetime}`, token, attrs);
        await mutateLessons();
        globalMutate(
            (key: unknown) => typeof key === 'string' && key.includes('/reports/most-indebted'),
            undefined,
            { revalidate: true }
        );
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (clientError) return <div>{t('label.error')}: {(clientError as Error).message}</div>;

    const client = clientData?.client;
    if (!client) return <div>{t('clients.not_found')}</div>;

    return (
        <VStack gap={6} align="stretch">
            <Box>
                <HStack justify="space-between" align="start">
                    <Box>
                        <Heading size="lg">{client.name}</Heading>
                        <Text color="gray.500" fontSize="sm">{client.id}</Text>
                    </Box>
                    <HStack>
                        <Button size="sm" variant="outline" onClick={() => setEditClientOpen(true)}>{t('client_detail.edit')}</Button>
                        <Button size="sm" variant="outline" colorScheme="telegram" onClick={() => {
                            const botUsername = TELEGRAM_BOT_USERNAME;
                            const link = `https://t.me/${botUsername}?start=${client.id}`;
                            navigator.clipboard.writeText(link);
                            // Open the link so it works on mobile (opens Telegram app)
                            // and on desktop (opens Telegram web / prompts app)
                            window.open(link, '_blank');
                            alert(t('telegram.link_copied'));
                        }}>
                            {t('telegram.connect')}
                        </Button>
                    </HStack>
                </HStack>
                <HStack mt={2} gap={4} flexWrap="wrap">
                    {client.email && <Text fontSize="sm"><a href={`mailto:${client.email}`}>{client.email}</a></Text>}
                    {client.phone && <Text fontSize="sm">{client.phone}</Text>}
                    {client.telegram && <Text fontSize="sm">Telegram: {client.telegram}</Text>}
                    {client.instagram && <Text fontSize="sm">Instagram: {client.instagram}</Text>}
                    {client.comment && <Text fontSize="sm" color="gray.500">{client.comment}</Text>}
                    <Text fontSize="sm" color="gray.400">{t('label.came_from')}: {cameFromLabels[client.came_from] ?? client.came_from}</Text>
                </HStack>
            </Box>

            <Card.Root>
                <Card.Body>
                    <Heading size="sm" mb={3}>{t('label.balance')}</Heading>
                    <HStack gap={8} flexWrap="wrap">
                        <Stat.Root><Stat.Label>{t('label.paid')}</Stat.Label><Stat.ValueText>{balance.paid} ₴</Stat.ValueText></Stat.Root>
                        <Stat.Root><Stat.Label>{t('label.used')}</Stat.Label><Stat.ValueText>{balance.used} ₴</Stat.ValueText></Stat.Root>
                        <Stat.Root><Stat.Label>{t('label.pending')}</Stat.Label><Stat.ValueText>{balance.pending} ₴</Stat.ValueText></Stat.Root>
                        <Stat.Root>
                            <Stat.Label>{t('label.available')}</Stat.Label>
                            <Stat.ValueText color={balance.available >= 0 ? 'green.500' : 'red.500'}>{balance.available} ₴</Stat.ValueText>
                        </Stat.Root>
                    </HStack>
                </Card.Body>
            </Card.Root>

            <Box>
                <HStack justify="space-between" mb={3}>
                    <Heading size="md">{t('label.students')}</Heading>
                    <Button size="sm" onClick={() => setAddStudentOpen(true)}>{t('client_detail.add_student')}</Button>
                </HStack>
                {sortedStudents.length === 0 ? <Text color="gray.500">{t('client_detail.no_students')}</Text> : (
                    <Table.Root variant="line" size="sm">
                        <Table.Header><Table.Row>
                            <Table.ColumnHeader>{t('label.name')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.date_of_birth')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.comment')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                        </Table.Row></Table.Header>
                        <Table.Body>
                            {sortedStudents.map(s => (
                                <Table.Row key={s.id}>
                                    <Table.Cell>{s.name}</Table.Cell>
                                    <Table.Cell>{s.date_of_birth ? formatDate(s.date_of_birth) : '—'}</Table.Cell>
                                    <Table.Cell>{s.comment ?? '—'}</Table.Cell>
                                    <Table.Cell>
                                        <HStack gap={1}>
                                            <Tooltip.Root>
                                                <Tooltip.Trigger asChild>
                                                    <Button size="xs" variant="ghost" onClick={() => { setAddLessonPresetStudent(s.id); setAddLessonOpen(true); }} aria-label={t('client_detail.add_lesson')}>📚</Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Positioner>
                                                    <Tooltip.Content>{t('client_detail.add_lesson')}</Tooltip.Content>
                                                </Tooltip.Positioner>
                                            </Tooltip.Root>
                                            <Tooltip.Root>
                                                <Tooltip.Trigger asChild>
                                                    <Button size="xs" variant="ghost" onClick={() => { setAddPaymentOpen(true); }} aria-label={t('client_detail.add_payment')}>💰</Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Positioner>
                                                    <Tooltip.Content>{t('client_detail.add_payment')}</Tooltip.Content>
                                                </Tooltip.Positioner>
                                            </Tooltip.Root>
                                            <Tooltip.Root>
                                                <Tooltip.Trigger asChild>
                                                    <Button size="xs" variant="ghost" onClick={() => setEditStudent(s)} aria-label={t('action.edit')}>✏️</Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Positioner>
                                                    <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                                </Tooltip.Positioner>
                                            </Tooltip.Root>
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                )}
            </Box>

            <Box>
                <HStack justify="space-between" mb={3}>
                    <Heading size="md">{t('nav.payments')}</Heading>
                    <Button size="sm" onClick={() => setAddPaymentOpen(true)}>{t('client_detail.add_payment')}</Button>
                </HStack>
                {paymentsLoading ? <VStack gap={2}>{[1, 2, 3].map(i => <Skeleton key={i} height="32px" />)}</VStack> : sortedPayments.length === 0 ? <Text color="gray.500">{t('client_detail.no_payments')}</Text> : (
                    <Table.Root variant="line" size="sm">
                        <Table.Header><Table.Row>
                            <Table.ColumnHeader>{t('label.amount')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.payment_by')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.comment')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                        </Table.Row></Table.Header>
                        <Table.Body>
                            {sortedPayments.map(p => (
                                <Table.Row key={p.id}>
                                    <Table.Cell>{p.amount} ₴</Table.Cell>
                                    <Table.Cell>{formatDate(p.date_time)}</Table.Cell>
                                    <Table.Cell>{p.payment_by}</Table.Cell>
                                    <Table.Cell>{p.comment ?? '—'}</Table.Cell>
                                    <Table.Cell>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={() => navigate(`/payment/${p.id}`)} aria-label={t('action.edit')}>✏️</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                )}
            </Box>

            <Box>
                <HStack justify="space-between" mb={3}>
                    <Heading size="md">{t('nav.lessons')}</Heading>
                    {sortedStudents.length > 0 && teachers.length > 0 && subjects.length > 0 && (
                        <Button size="sm" onClick={() => { setAddLessonPresetStudent(null); setAddLessonOpen(true); }}>{t('client_detail.add_lesson')}</Button>
                    )}
                </HStack>
                {lessonsLoading ? <VStack gap={2}>{[1, 2, 3].map(i => <Skeleton key={i} height="32px" />)}</VStack> : sortedLessons.length === 0 ? <Text color="gray.500">{t('client_detail.no_lessons')}</Text> : (
                    <Table.Root variant="line" size="sm">
                        <Table.Header><Table.Row>
                            <Table.ColumnHeader>{t('label.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.subject')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.duration')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.price')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.status')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.teacher')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                        </Table.Row></Table.Header>
                        <Table.Body>
                            {sortedLessons.map(l => (
                                <Table.Row key={`${l.student_id}:${l.teacher_id}:${l.date_time_local}`}>
                                    <Table.Cell>{l.date_time_local}</Table.Cell>
                                    <Table.Cell>{getSubjectPrettyName(l.subject)}</Table.Cell>
                                    <Table.Cell>{l.duration_minutes} min</Table.Cell>
                                    <Table.Cell>{l.price_uah} ₴</Table.Cell>
                                    <Table.Cell>{l.completed ? t('lessons.done') : t('lessons.pending')}</Table.Cell>
                                    <Table.Cell><Text fontSize="xs">{teacherMap[l.teacher_id]?.name ?? l.teacher_id.slice(0, 8) + '…'}</Text></Table.Cell>
                                    <Table.Cell>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={() => setEditLesson(l)} aria-label={t('action.edit')}>✏️</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                )}
            </Box>

            {editClientOpen && <ClientEditDrawer client={client} open={editClientOpen} onClose={() => setEditClientOpen(false)} onSave={handleEditClient} />}
            <StudentFormDrawer title={t('student.add')} initial={{}} open={addStudentOpen} onClose={() => setAddStudentOpen(false)} onSave={handleAddStudent} />
            {editStudent && <StudentFormDrawer title={`${t('student.edit')}: ${editStudent.name}`} initial={{ name: editStudent.name, date_of_birth: editStudent.date_of_birth, comment: editStudent.comment, deleted: editStudent.deleted }} open={!!editStudent} onClose={() => setEditStudent(null)} onSave={handleEditStudent} />}
            <PaymentFormDrawer title={t('client_detail.add_payment').replace('+ ', '')} initial={{ payment_by: 'cash' }} clientId={id!} open={addPaymentOpen} onClose={() => setAddPaymentOpen(false)} onSave={handleAddPayment} getAccessToken={getAccessTokenSilently} />
            {teachers.length > 0 && subjects.length > 0 && sortedStudents.length > 0 && <LessonFormDrawer title={t('lessons.new_lesson')} initial={addLessonPresetStudent ? { student_id: addLessonPresetStudent } : {}} students={sortedStudents} teachers={teachers} subjects={subjects} priceMap={priceMap} open={addLessonOpen} onClose={() => { setAddLessonPresetStudent(null); setAddLessonOpen(false); }} onSave={handleAddLesson} />}
            {editLesson && teachers.length > 0 && subjects.length > 0 && <LessonFormDrawer title={t('lessons.edit_title')} initial={{ student_id: editLesson.student_id, teacher_id: editLesson.teacher_id, date_time_local: editLesson.date_time_local, price_uah: editLesson.price_uah, duration_minutes: editLesson.duration_minutes, subject: editLesson.subject, individual: editLesson.individual, online: editLesson.online, completed: editLesson.completed, comment: editLesson.comment, deleted: editLesson.deleted }} students={sortedStudents} teachers={teachers} subjects={subjects} priceMap={priceMap} open={!!editLesson} onClose={() => setEditLesson(null)} onSave={handleEditLesson} />}
        </VStack>
    );
}
